import type { DerivedAggregates, WorldState } from "../engine/types";
import type {
  IndicatorDef,
  TrendDirection,
  Verdict,
} from "../engine/indicators";
import { OrnamentChimney } from "./Ornaments";
import {
  formatDeltaValue,
  INDICATORS,
  sparklineFor,
  trendFor,
  VERDICT_LABEL,
} from "../engine/indicators";
import { useAnimatedNumber } from "../engine/animations";
import { useGame } from "../store/useGame";

interface Props {
  state: WorldState;
  aggregates: DerivedAggregates;
}

/**
 * Indicateurs « en une » : rendus comme un article principal (≈2/3 de
 * largeur, gros chiffre + accroche, le détail au clic). Les autres se lisent
 * comme des cotations autour. Pour l'instant, l'approbation — la mesure
 * cardinale d'un président — fait la une.
 */
const FEATURED_IDS = new Set(["approval"]);

/** Accroche d'article pour un indicateur en une, selon son verdict. */
const ACCROCHE: Record<string, Record<Verdict, string>> = {
  approval: {
    excellent: "Une popularité que peu de présidents ont connue.",
    sain: "Une majorité d'Hexagonaux suit encore l'exécutif.",
    tenable: "Le socle tient — sans ferveur.",
    preoccupant: "La défiance gagne du terrain, mois après mois.",
    critique: "La rue gronde : la chute n'est plus une hypothèse.",
  },
};

/** « Agir » — seuils de verdict à partir desquels l'action est proposée. */
const ACTIONABLE: Set<Verdict> = new Set(["preoccupant", "critique"]);

export function IndicatorStrip({ state, aggregates }: Props) {
  const history = useGame((s) => s.indicatorHistory);
  const openIndicator = useGame((s) => s.openIndicator);
  const decisions = useGame((s) => s.decisions);
  const played = useGame((s) => s.playedDecisions);
  const actOnIndicator = useGame((s) => s.actOnIndicator);

  // G4 — onboarding visuel : on ne montre que les indicateurs dont le
  // tour minimum est atteint.
  const visible = INDICATORS.filter(
    (ind) => state.turn >= (ind.revealedFromTurn ?? 0),
  );

  // « Agir » — indicateurs ayant encore au moins un redressement réservé
  // non consommé.
  const remediable = new Set(
    decisions
      .filter((d) => d.remediation_for && !played.includes(d.id))
      .map((d) => d.remediation_for as string),
  );

  return (
    <section className="indicator-strip" aria-label="Où en est le Président ?">
      <div className="rule-dotted section-rubric-rule" aria-hidden="true" />
      <header className="section-rubric">
        <OrnamentChimney size={40} className="section-vignette" />
        <h2 className="section-rubric-title">Où en est le Président&nbsp;?</h2>
      </header>
      <div className="indicator-grid">
        {visible.map((ind) => (
          <IndicatorCell
            key={ind.id}
            def={ind}
            state={state}
            aggregates={aggregates}
            history={history[ind.id] ?? []}
            featured={FEATURED_IDS.has(ind.id)}
            canAct={remediable.has(ind.id)}
            onOpen={() => openIndicator(ind.id)}
            onAct={() => actOnIndicator(ind.id)}
            freshlyRevealed={
              state.turn === (ind.revealedFromTurn ?? 0) &&
              (ind.revealedFromTurn ?? 0) > 0
            }
          />
        ))}
      </div>
    </section>
  );
}

interface CellProps {
  def: IndicatorDef;
  state: WorldState;
  aggregates: DerivedAggregates;
  history: { turn: number; value: number }[];
  featured: boolean;
  canAct: boolean;
  onOpen: () => void;
  onAct: () => void;
  freshlyRevealed: boolean;
}

function IndicatorCell({
  def,
  state,
  aggregates,
  history,
  featured,
  canAct,
  onOpen,
  onAct,
  freshlyRevealed,
}: CellProps) {
  const value = def.extract(state, aggregates);
  // G1 : le chiffre se déroule au lieu de sauter.
  const animatedValue = useAnimatedNumber(value, 380);
  const trend = trendFor(history);
  const spark = sparklineFor(history);
  const alert =
    (def.alertAbove !== undefined && value > def.alertAbove) ||
    (def.alertBelow !== undefined && value < def.alertBelow);

  // E2 — verdict qualitatif + variation depuis le tour précédent.
  const verdict = def.verdict ? def.verdict(value) : null;
  const previousValue =
    history.length >= 2
      ? history[history.length - 2].value
      : history.length === 1
        ? history[0].value
        : null;
  const delta = previousValue !== null ? value - previousValue : null;
  // Formatage et seuil de bruit partagés avec le panneau « Ce tour-ci »
  // (engine/indicators.ts) — seuils relâchés au sprint lisibilité.
  const formattedDelta = formatDeltaValue(def, delta);
  const accroche =
    featured && verdict ? ACCROCHE[def.id]?.[verdict] : undefined;
  // « Agir » — proposé quand l'indicateur va mal ET qu'un levier reste.
  const showAct = canAct && verdict !== null && ACTIONABLE.has(verdict);

  return (
    <div
      className={`indicator ${featured ? "indicator-featured" : ""} ${alert ? "indicator-alert" : ""} ${freshlyRevealed ? "indicator-appear" : ""}`}
      data-indicator-id={def.id}
      data-verdict={verdict ?? undefined}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        // Ne réagir qu'au focus sur la carte elle-même : sinon Entrée/Espace
        // sur le bouton « Agir » imbriqué ouvrirait AUSSI le détail (le
        // keydown remonte ; stopPropagation du onClick ne couvre pas le clavier).
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      aria-label={`${def.label} : ${def.format(value)}${def.unit}${verdict ? ", " + VERDICT_LABEL[verdict] : ""}, appuyer pour plus de détails`}
      title={def.description}
    >
      <div className="ind-label">{def.label}</div>
      <div className="ind-value">
        {def.format(animatedValue)}
        {def.unit && <span className="ind-unit">{def.unit}</span>}
      </div>
      {verdict && <div className="ind-verdict">{VERDICT_LABEL[verdict]}</div>}
      {accroche && <p className="ind-accroche">{accroche}</p>}
      {def.id === "approval" && (
        <ApprovalTruth approval={value} aggregates={aggregates} />
      )}
      {formattedDelta && (
        <div
          className="ind-delta"
          data-direction={
            delta && delta > 0 ? "up" : delta && delta < 0 ? "down" : "flat"
          }
        >
          {formattedDelta}
        </div>
      )}
      <div className={`ind-trend trend-${trend.direction}`}>
        {trendGlyph(trend.direction)}
      </div>
      <div className="ind-spark" aria-hidden="true">
        {spark}
      </div>
      {showAct && (
        <button
          type="button"
          className="indicator-act"
          onClick={(e) => {
            e.stopPropagation();
            onAct();
          }}
          aria-label={`Agir sur ${def.label}`}
        >
          ⚡ Agir
        </button>
      )}
    </div>
  );
}

/** Écart sondage/réel jugé « significatif » (aligné sur SectionArticles). */
const GAP_THRESHOLD = 8;

/**
 * Sous l'approbation (le sondage, biaisable par les médias et truqué en
 * dictature), on affiche le RESSENTI RÉEL des cohortes — la mesure qui
 * destitue vraiment. Rend lisible la doctrine « le pouvoir corrompt la
 * mesure » : un écart de façade signale que le chiffre affiché ment. Avant,
 * le ressenti réel n'existait que dans le drawer Société → le joueur pouvait
 * se croire au sommet en coulant.
 */
function ApprovalTruth({
  approval,
  aggregates,
}: {
  approval: number;
  aggregates: DerivedAggregates;
}) {
  const real = aggregates.meanSatisfaction;
  const gap = approval - real;
  const significant = Math.abs(gap) >= GAP_THRESHOLD;
  return (
    <div className="ind-approval-truth">
      <span className="ind-poll-tag">sondage</span>
      <span className="ind-real-sat">
        Ressenti réel <strong>{Math.round(real)}</strong>
      </span>
      {significant && (
        <span className="ind-gap" data-illusion={gap > 0 ? "true" : "false"}>
          {gap > 0
            ? `façade +${Math.round(gap)}`
            : `sous-coté −${Math.round(-gap)}`}
        </span>
      )}
    </div>
  );
}

function trendGlyph(direction: TrendDirection): string {
  if (direction === "up") return "↑";
  if (direction === "down") return "↓";
  return "→";
}
